package dev.lingvo.shared

import kotlinx.serialization.Serializable

/** Stored fit measurements allow the server to enforce export eligibility. */
@Serializable
data class RenderFitDto(val fontPx: Double, val floorPx: Double, val overflow: Boolean, val lines: Int)

/** An immutable render records the exact design, capture and caption it was composed from. */
@Serializable
data class RenderDto(
    val id: String, val slotId: String, val slotKey: String, val deviceClass: DeviceClass, val locale: String,
    val format: ImageFormat, val width: Int, val height: Int, val byteSize: Int, val sha256: String,
    val designVersion: Int, val captureId: String? = null, val captureSha256: String? = null,
    val captionSha256: String? = null, val fit: RenderFitDto, val renderedAt: String,
    val renderedBy: String, val imagePath: String,
)

/** Stable blockers explain why a render cannot be approved or shipped. */
@Serializable
enum class RenderBlock {
    CAPTION_NOT_APPROVED, CAPTION_CHANGED, CAPTION_OVERFLOWS, CAPTURE_REPLACED, CAPTURE_STALE,
    DESIGN_VERSION_UNAVAILABLE,
}

/** Warnings remain visible without invalidating an otherwise shippable render. */
@Serializable
enum class RenderWarning { DESIGN_CHANGED_SINCE_RENDER, CAPTURE_UNREVIEWED }

/** A check result preserves the affected cell for a direct repair link. */
@Serializable
data class RenderCheckDto(
    val renderId: String, val slotKey: String, val deviceClass: DeviceClass, val locale: String,
    val pushable: Boolean, val blocks: List<RenderBlock>, val warnings: List<RenderWarning>,
)

/** A cell is identified by the stable plan key rather than presentation order. */
@Serializable
data class RenderCellRef(val slotKey: String, val deviceClass: DeviceClass, val locale: String)

/** One report covers both existing renders and planned cells without a render. */
@Serializable
data class RenderCheckReportDto(
    val platform: StorePlatform, val designVersion: Int?, val renders: List<RenderCheckDto>, val missing: List<RenderCellRef>,
)

/** Approval pins both identity and bytes so replacement cannot inherit consent. */
@Serializable
data class ApprovedRenderRef(val renderId: String, val sha256: String)

/** An approval request explicitly names the reviewed set. */
@Serializable
data class ShotSetApprovalRequest(val renders: List<ApprovedRenderRef>)

/** Invalidation reasons remain machine readable as inputs change after approval. */
@Serializable
enum class ApprovalInvalidReason {
    RENDER_REPLACED, RENDER_DELETED, CAPTION_CHANGED, CAPTION_UNAPPROVED, CAPTURE_CHANGED, REVOKED,
    DESIGN_VERSION_UNAVAILABLE,
}

/** The approved snapshot survives render replacement or deletion. */
@Serializable
data class ApprovalItemDto(val cell: RenderCellRef, val renderId: String, val sha256: String)

/** Invalid items direct reviewers to exactly the cell that needs renewed consent. */
@Serializable
data class ApprovalInvalidItemDto(val cell: RenderCellRef, val reason: ApprovalInvalidReason)

/** Validity is recomputed while preserving the original approval audit record. */
@Serializable
data class ShotSetApprovalDto(
    val id: String, val platform: StorePlatform, val approvedBy: String, val approvedAt: String,
    val items: List<ApprovalItemDto>, val valid: Boolean, val invalid: List<ApprovalInvalidItemDto> = emptyList(),
    val revokedAt: String? = null,
)
